import React from "react";
import Button, { ButtonType } from "../Button";
import { trialDurationHyphenated } from "@functions/plans/trialConstants";

const TRIAL_DOCS_URL = "https://docs.roboflow.com/billing/premium-trial";

type TrialStartBannerProps = {
    title: string;
    description: string;
    onStartTrial: () => void;
    isLoading: boolean;
    className?: string;
};

export function TrialStartBanner({
    title,
    description,
    onStartTrial,
    isLoading,
    className = ""
}: TrialStartBannerProps) {
    return (
        <div
            className={`flex w-full flex-col items-start justify-between gap-4 rounded-lg border border-yellow-200 bg-gradient-to-r from-yellow-50 to-orange-50 p-4 md:flex-row md:items-center ${className}`}
        >
            <div className="flex flex-col gap-1 text-left">
                <div className="text-base font-semibold text-gray-900">{title}</div>
                <div className="text-sm text-gray-700">
                    {description}{" "}
                    <a
                        href={TRIAL_DOCS_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium text-brand hover:text-brand-hover"
                    >
                        Learn more
                    </a>
                </div>
            </div>
            <Button
                type="upgrade"
                onClick={onStartTrial}
                loading={isLoading}
                className="w-full md:w-auto"
            >
                {`Start my ${trialDurationHyphenated} trial`}
            </Button>
        </div>
    );
}

export type CTADescriptor = {
    label: string;
    icon?: string;
    onClick: () => void;
    loading?: boolean;
    rank: "primary" | "secondary" | "tertiary";
};

type BuildUpsellCTAActionsParams = {
    showTrialCTA: boolean;
    showUpgradeCTA: boolean;
    trialButtonLabel?: string;
    upgradeCTALabel?: string;
    isActivatingTrial: boolean;
    activateTrial: () => void;
    openUpgradeOrPricing: () => void;
    handleSalesFormClick: () => void;
};

export function buildUpsellCTAActions({
    showTrialCTA,
    showUpgradeCTA,
    trialButtonLabel = "Try it for free with a trial",
    upgradeCTALabel = "Upgrade Plan",
    isActivatingTrial,
    activateTrial,
    openUpgradeOrPricing,
    handleSalesFormClick
}: BuildUpsellCTAActionsParams): CTADescriptor[] {
    const actions: CTADescriptor[] = [];

    if (showTrialCTA) {
        actions.push({
            label: trialButtonLabel,
            icon: "fa-regular fa-sparkles",
            onClick: activateTrial,
            loading: isActivatingTrial,
            rank: "primary"
        });
        actions.push({
            label: "View Pricing",
            icon: "fa-regular fa-arrow-up-from-bracket",
            onClick: openUpgradeOrPricing,
            rank: "secondary"
        });
        actions.push({
            label: "Talk to Sales",
            icon: "fa-regular fa-message",
            onClick: handleSalesFormClick,
            rank: "tertiary"
        });
    } else if (showUpgradeCTA) {
        actions.push({
            label: upgradeCTALabel,
            icon: "fa-regular fa-arrow-up-from-bracket",
            onClick: openUpgradeOrPricing,
            rank: "primary"
        });
        actions.push({
            label: "Talk to Sales",
            icon: "fa-regular fa-message",
            onClick: handleSalesFormClick,
            rank: "secondary"
        });
    } else {
        actions.push({
            label: "Talk to Sales",
            icon: "fa-regular fa-message",
            onClick: handleSalesFormClick,
            rank: "primary"
        });
    }

    return actions;
}

type RankedCTAGroupProps = { actions: CTADescriptor[] };

const RANK_TO_BUTTON_TYPE: Record<CTADescriptor["rank"], ButtonType> = {
    primary: "upgrade",
    secondary: "secondary",
    tertiary: "ghost"
};

export function RankedCTAGroup({ actions }: RankedCTAGroupProps) {
    const primary = actions.find((a) => a.rank === "primary");
    const secondary = actions.find((a) => a.rank === "secondary");
    const tertiaries = actions.filter((a) => a.rank === "tertiary");

    const hasSideBySide = primary && secondary;

    return (
        <div className="flex w-full flex-col gap-2">
            {hasSideBySide ? (
                <div className="flex w-full flex-col gap-2 md:flex-row">
                    <CTAButton action={primary} className="w-full md:flex-1" />
                    <CTAButton action={secondary} className="w-full md:flex-1" />
                </div>
            ) : primary ? (
                <CTAButton action={primary} className="w-full" />
            ) : secondary ? (
                <CTAButton action={secondary} className="w-full" />
            ) : null}

            {tertiaries.map((action) => (
                <CTAButton key={action.label} action={action} className="w-full" />
            ))}
        </div>
    );
}

function CTAButton({ action, className }: { action: CTADescriptor; className?: string }) {
    return (
        <Button
            type={RANK_TO_BUTTON_TYPE[action.rank]}
            onClick={action.onClick}
            loading={action.loading}
            className={className}
        >
            {action.icon && <i className={action.icon} />}
            {action.label}
        </Button>
    );
}
